import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { Project, WageEntry, SubContract } from "../../data/mockData";
import { ProjectFinancials } from "../../engine/projectFinancialsEngine";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";
import { subContractDisplayLabelPretty } from "../../utils/format";


type EnrichedSubContract = SubContract & { balance?: number; paidToDate?: number };

type Props = {
  project: Project;
  financials: ProjectFinancials;
  subContracts?: EnrichedSubContract[];
  wages?: WageEntry[];
  // add these
  clients?: any[];
  resources?: any[];
};

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function pct(n: number) {
  if (!Number.isFinite(n)) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

// Balance fallback (keep!)
function computeSubcontractBalance(sc: any): number {
  const direct = Number(sc?.balance);
  if (Number.isFinite(direct)) return direct;

  const total =
    Number(sc?.contractAmount) ||
    Number(sc?.total) ||
    Number(sc?.totalCost) ||
    Number(sc?.agreedAmount) ||
    Number(sc?.amount) ||
    0;

  const paid = Number(sc?.paidToDate) || Number(sc?.paid) || 0;

  const b = total - paid;
  return Number.isFinite(b) ? b : 0;
}

export default function OverviewTab({
  project,
  financials,
  subContracts = [],
  wages = [],
  clients = [],
  resources = [],
}: Props) {
  const { t } = useTranslation();
  const { totals } = financials;
  const method = (totals as any).method;

  const outstandingSubcontracts = useMemo(() => {
    return subContracts.reduce((s, sc: any) => s + computeSubcontractBalance(sc), 0);
  }, [subContracts]);

  // total subcontract value (for "out of")
  const totalSubcontractValue = useMemo(() => {
    return subContracts.reduce((s: number, sc: any) => {
      return (
        s +
        (Number(sc?.contractAmount) ||
          Number(sc?.total) ||
          Number(sc?.totalCost) ||
          Number(sc?.agreedAmount) ||
          Number(sc?.amount) ||
          0)
      );
    }, 0);
  }, [subContracts]);

  const dbSubcontractors = useMemo(() => {
    return (resources || [])
      .filter((r: any) => Number(r.isSubcontractor ?? r.is_subcontractor ?? 0) === 1)
      .map((r: any) => ({ id: r.id, name: r.name }));
  }, [resources]);


  const unpaidWages = useMemo(() => {
    return wages
      .filter((w: any) => !w.paid)
      .reduce((s, w) => s + (Number((w as any).totalWages) || 0), 0);
  }, [wages]);

  const wagesPaid = useMemo(() => {
    return wages
      .filter((w: any) => !!w.paid)
      .reduce((s, w) => s + (Number((w as any).totalWages) || 0), 0);
  }, [wages]);

  const remainingToCollect = (Number(totals.totalEstimate) || 0) - (Number(totals.totalReceived) || 0);
  const hasLiabilities = outstandingSubcontracts > 0 || unpaidWages > 0;

  const outstandingList = useMemo(() => {
  const rows = subContracts.map((sc: any) => ({
    sc,
    balance: computeSubcontractBalance(sc),
  }));

    // Unpaid first, then paid-in-full, and within each group sort by largest balance
    rows.sort((a, b) => {
      const aUnpaid = a.balance > 0.0001 ? 1 : 0;
      const bUnpaid = b.balance > 0.0001 ? 1 : 0;
      if (aUnpaid !== bUnpaid) return bUnpaid - aUnpaid;
      return b.balance - a.balance;
    });

    return rows;
  }, [subContracts]);


  // ------------------------------
  // CORE METRICS (Revenue / Costs / Profit)
  // ------------------------------

  // Revenue
  const totalEstimate = Number(totals.totalEstimate) || 0;
  const totalReceived = Number(totals.totalReceived) || 0;

  const totalReceivedCash =
    Number(method?.cashIn ?? method?.cashReceived ?? method?.cashInTotal ?? 0) || 0;

  const totalReceivedBank =
    Number(method?.bankIn ?? method?.bankReceived ?? method?.bankInTotal ?? 0) || 0;

  const remainingToCollectCore = totalEstimate - totalReceived;

  // Costs (paid-to-date, correct buckets)
  const subContractsPaid = Number((totals as any).subcontractPaidTotal) || 0;
  const expensesOnly = Number((totals as any).expensesOtherTotal) || 0;

  // Profit (cash reality): Received − (paid wages + paid subs + expenses)
  const trueCostsPaidToDate = wagesPaid + subContractsPaid + expensesOnly;
  const currentProfit = totalReceived - trueCostsPaidToDate;
  const currentProfitMargin = totalReceived > 0 ? currentProfit / totalReceived : 0;

  return (
    <div className="container" style={{ padding: 0 }}>
      {/* Page-scoped responsive grid helpers */}
      <style>{`
        .ovHeroQuick {
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-top: 12px;
        }
        @media (max-width: 980px) {
          .ovHeroQuick { grid-template-columns: 1fr; }
        }

        .ovMiniRow {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          padding: 8px 10px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.70);
        }
        .ovMiniLabel { color: var(--muted); font-weight: 900; font-size: 12px; }
        .ovMiniValue { font-weight: 1000; letter-spacing: -0.2px; }
        .ovMiniOutOf { color: var(--muted); font-weight: 800; margin-left: 8px; font-size: 12px; }

        .ovCore3 {
          display: grid;
          gap: 14px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-top: 14px;
        }
        @media (max-width: 980px) {
          .ovCore3 { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* HERO / SUMMARY */}
      <Card
        title={
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 18, fontWeight: 1000, letterSpacing: "-0.2px" }}>
              {project.name}
            </span>
            <span
              className={`badge ${hasLiabilities ? "badgeWarn" : "badgeGood"}`}
              title={t("projectDetail.overview.liabilitiesTip")}
            >
              {hasLiabilities ? t("projectDetail.overview.needsAttention") : t("projectDetail.overview.closeReady")}
            </span>
          </div>
        }
        subtitle={t("projectDetail.overview.subtitle")}
      >
        <div className="ovHeroQuick">
          <div className="ovMiniRow">
            <div className="ovMiniLabel">{t("projectDetail.overview.outstandingSubs")}</div>
            <div className={`ovMiniValue ${outstandingSubcontracts > 0 ? "numBad" : "numGood"}`}>
              {money(outstandingSubcontracts)}
              <span className="ovMiniOutOf">{t("projectDetail.overview.outOf")} {money(totalSubcontractValue)}</span>
            </div>
          </div>

          <div className="ovMiniRow">
            <div className="ovMiniLabel">{t("projectDetail.overview.unpaidWages")}</div>
            <div className={`ovMiniValue ${unpaidWages > 0 ? "numBad" : "numGood"}`}>
              {money(unpaidWages)}
            </div>
          </div>

          <div className="ovMiniRow">
            <div className="ovMiniLabel">{t("projectDetail.overview.remainingToCollect")}</div>
            <div className={`ovMiniValue ${remainingToCollect > 0 ? "" : "numGood"}`}>
              {money(remainingToCollect)}
              <span className="ovMiniOutOf">{t("projectDetail.overview.outOf")} {money(totalEstimate)}</span>
            </div>
          </div>
        </div>

        {hasLiabilities ? (
          <div className="noteOnCard" style={{ marginTop: 12 }}>
            {t("projectDetail.overview.hasLiabilitiesNote")}
          </div>
        ) : (
          <div className="noteOnCard" style={{ marginTop: 12 }}>
            {t("projectDetail.overview.noLiabilitiesNote")}
          </div>
        )}
      </Card>

      {/* Core Metrics as 3 separate cards */}
      <div className="ovCore3">
        <Card title={t("projectDetail.overview.revenue")} subtitle={t("projectDetail.overview.revenueSub")}>
          <SummaryRow label={t("projectDetail.overview.totalEstimate")} value={totalEstimate} />

          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span style={{ color: "var(--muted)" }}>{t("projectDetail.overview.totalReceived")}</span>
            <span style={{ fontWeight: 1000 }}>
              {t("common.cash")} {money(totalReceivedCash)}{" "}
              <span style={{ color: "var(--muted)" }}>|</span>{" "}
              {t("common.bank")} {money(totalReceivedBank)}
            </span>
          </div>

          <SummaryRow
            label={t("projectDetail.overview.remainingToCollect")}
            value={remainingToCollectCore}
            tone={remainingToCollectCore > 0 ? "warn" : "good"}
          />
        </Card>

        <Card title={t("projectDetail.overview.trueCostsTitle")} subtitle={t("projectDetail.overview.trueCostsSub")}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span style={{ color: "var(--muted)" }}>{t("projectDetail.overview.subContractsPaid")}</span>
            <span style={{ fontWeight: 1000 }}>{money(subContractsPaid)}</span>
          </div>

          <SummaryRow label={t("projectDetail.overview.wagesPaid")} value={wagesPaid} />
          <SummaryRow label={t("dashboard.expenses")} value={expensesOnly} />

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            {t("projectDetail.overview.expensesNote")}
          </div>
        </Card>

        <Card title={t("projectDetail.overview.profitTitle")} subtitle={t("projectDetail.overview.profitSub")}>
          <SummaryRow
            label={t("projectDetail.overview.currentProfit")}
            value={currentProfit}
            tone={currentProfit < 0 ? "bad" : "good"}
          />
          <Row2 label={t("projectDetail.overview.currentMargin")} right={pct(currentProfitMargin)} emphasize />

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            {t("projectDetail.overview.profitNote")}
          </div>
        </Card>
      </div>

      {/* Cash/Bank Pools */}
      {method ? (
        <div style={{ marginTop: 14 }}>
          <Card title={t("projectDetail.overview.cashBank")} subtitle={t("projectDetail.overview.cashBankSub")}>
            <TableShell title={null} subtitle={null}>
              <table>
                <thead>
                  <tr>
                    <th />
                    <th>{t("common.cash")}</th>
                    <th>{t("common.bank")}</th>
                    <th>{t("common.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>{t("projectDetail.overview.received")}</strong>
                    </td>
                    <td>{money(Number(method.cashIn) || 0)}</td>
                    <td>{money(Number(method.bankIn) || 0)}</td>
                    <td>{money((Number(method.cashIn) || 0) + (Number(method.bankIn) || 0))}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("projectDetail.overview.trueCosts")}</strong>
                    </td>
                    <td>{money(Number(method.cashOut) || 0)}</td>
                    <td>{money(Number(method.bankOut) || 0)}</td>
                    <td>{money((Number(method.cashOut) || 0) + (Number(method.bankOut) || 0))}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("projectDetail.overview.netTransfers")}</strong></td>
                    <td>{money((Number(method.transfersInCash) || 0) - (Number(method.transfersOutCash) || 0))}</td>
                    <td>{money((Number(method.transfersInBank) || 0) - (Number(method.transfersOutBank) || 0))}</td>
                    <td>{money(
                      (Number(method.transfersInCash) || 0) + (Number(method.transfersInBank) || 0)
                      - (Number(method.transfersOutCash) || 0) - (Number(method.transfersOutBank) || 0)
                    )}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("dashboard.surplus")}</strong>
                    </td>
                    <td style={{ fontWeight: 1000 }}>{money(Number(method.cashSurplus) || 0)}</td>
                    <td style={{ fontWeight: 1000 }}>{money(Number(method.bankSurplus) || 0)}</td>
                    <td style={{ fontWeight: 1000 }}>
                      {money((Number(method.cashSurplus) || 0) + (Number(method.bankSurplus) || 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </TableShell>

            <div className="pageNote" style={{ marginTop: 10 }}>
              {t("projectDetail.overview.poolNote")}
            </div>
          </Card>
        </div>
      ) : null}

      {/* Subcontract audit ALWAYS shown (no toggle) */}
      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("projectDetail.overview.subBalances")}
          subtitle={(() => {
            const unpaid = outstandingList.filter((x) => x.balance > 0.0001).length;
            const paid = outstandingList.length - unpaid;

            if (!outstandingList.length) return t("projectDetail.overview.noSubcontracts");
            if (unpaid === 0) return t("projectDetail.overview.allPaid", { count: paid });
            return t("projectDetail.overview.someUnpaid", { unpaid, paid, total: outstandingList.length });
          })()}

          right={<span className="badge">{t("projectDetail.overview.balanceAudit")}</span>}
        >
          <table>
            <thead>
              <tr>
                <th style={{ width: 120 }}>{t("projectDetail.overview.subId")}</th>
                <th>{t("projectDetail.overview.subcontract")}</th>
                <th style={{ width: 160, textAlign: "right" }}>{t("projectDetail.overview.unpaidBalance")}</th>
                <th style={{ width: 160, textAlign: "right" }}>{t("projectDetail.overview.paidToDate")}</th>
                <th style={{ width: 160, textAlign: "right" }}>{t("projectDetail.overview.totalAgreed")}</th>
              </tr>
            </thead>
            <tbody>
              {outstandingList.map(({ sc, balance }) => {
                const paid = Number((sc as any).paidToDate) || 0;
                const total =
                  Number((sc as any).contractAmount) ||
                  Number((sc as any).total) ||
                  Number((sc as any).totalCost) ||
                  Number((sc as any).agreedAmount) ||
                  Number((sc as any).amount) ||
                  0;

                return (
                  <tr key={(sc as any).id}>
                    <td>{(sc as any).id}</td>
                    <td>
                      <CellClamp
                          lines={2}
                          title={subContractDisplayLabelPretty(sc, [project], clients, dbSubcontractors)}
                        >
                          <span style={{ fontWeight: 900 }}>
                            {subContractDisplayLabelPretty(sc, [project], clients, dbSubcontractors)}
                          </span>
                        </CellClamp>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {balance > 0.0001 ? (
                        <span className="numBad">{money(balance)}</span>
                      ) : (
                        <span className="numGood">
                          {money(0)}
                          <span className="muted" style={{ marginLeft: 8, fontWeight: 900 }}>
                            {t("projectDetail.overview.paidFull")}
                          </span>
                        </span>
                      )}
                    </td>

                    <td style={{ textAlign: "right" }}>{money(paid)}</td>
                    <td style={{ textAlign: "right" }}>{money(total)}</td>
                  </tr>
                );
              })}

              {outstandingList.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                    {t("projectDetail.overview.noSubcontracts")}
                  </td>
                </tr>
              )}

            </tbody>
          </table>
        </TableShell>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("projectDetail.overview.closeReadyNote")}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "neutral" | "good" | "warn" | "bad";
}) {
  const cls =
    tone === "good" ? "numGood" : tone === "bad" ? "numBad" : tone === "warn" ? "numWarn" : "";

  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span className={cls} style={{ fontWeight: 1000 }}>
        {money(value)}
      </span>
    </div>
  );
}

function Row2({
  label,
  right,
  emphasize,
}: {
  label: string;
  right: string;
  emphasize?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span style={{ fontWeight: emphasize ? 1000 : 800 }}>{right}</span>
    </div>
  );
}

